import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, map, Observable, of, tap, throwError } from 'rxjs';
import {
  ApiResponse,
  JwtLoginResponse,
  LoginRequest,
  Permission,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateUserProfile,
  User,
  UserProfile,
} from '../model/auth.interfaces';
import { AuthConfigService } from './auth-config.service';
import { AuthStoreService } from './auth-store.service';
import { TokenStorageService } from './token-storage.service';
import { HttpClient } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { TokenRefreshService } from './token-refresh.service';
import { TokenExpirationMonitorService } from './token-expiration-monitor.service';

export interface UserRole {
  id: number;
  name: string;
}

export interface ActiveRole {
  id: number;
  name: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authStore = inject(AuthStoreService);
  private tokenStorage = inject(TokenStorageService);
  private http = inject(HttpClient);
  private config = inject(AuthConfigService);
  private store = inject(Store);
  private tokenRefreshService = inject(TokenRefreshService);
  private tokenExpirationMonitor = inject(TokenExpirationMonitorService);

  private permissionsSubject = new BehaviorSubject<Permission[]>([]);
  public permissions$ = this.permissionsSubject.asObservable();
  private userRolesSubject = new BehaviorSubject<string[]>([]);
  public userRoles$ = this.userRolesSubject.asObservable();
  private readonly activeRole$ = new BehaviorSubject<ActiveRole | null>(null);
  private readonly activeRoleSubject = new BehaviorSubject<UserRole | null>(null);

  constructor() {
    this.checkAuthentication();
  }

  /**
   * Observable para escuchar cambios en permisos
   */
  get permissionsChanges(): Observable<Permission[]> {
    return this.permissions$;
  }

  login(email: string, password: string): Observable<boolean> {
    const loginRequest: LoginRequest = { email, password };

    return this.http
      .post<JwtLoginResponse>(this.config.getEndpointUrl('loginEndpoint'), loginRequest)
      .pipe(
        map((response) => {
          //console.log('AuthService - Respuesta exitosa:', response);

          if (response && response.access) {
            const token = response.access;
            const refreshToken = response.refresh;

            const user: User = {
              user_id: response.user_id || 0,
              provider_id: response.provider_id || 0,
              email: response.email,
              username: response.username,
              roles: response.roles,
            };

            //console.log('🔐 AuthService - Guardando datos:', user);

            this.tokenStorage.setToken(token);

            if (refreshToken) {
              this.tokenStorage.setRefreshToken(refreshToken);
            }

            // Guardar usuario en localStorage para persistencia entre sesiones
            localStorage.setItem('auth_user', JSON.stringify(user));

            // Actualizar el Subject con los nuevos roles
            this.userRolesSubject.next(response.roles);

            // Guardar usuario en el store (única fuente de verdad durante la sesión activa)
            this.authStore.setUser(user);

            // Guardar permisos y notificar cambios
            if (response.permissions) {
              //console.log('🔑 Guardando nuevos permisos:', response.permissions);
              localStorage.setItem('user_permissions', JSON.stringify(response.permissions));
              this.permissionsSubject.next(response.permissions);
            } else {
              // Si no hay permisos, limpiar
              //localStorage.removeItem('user_permissions');
              this.permissionsSubject.next([]);
            }

            return true;
          }
          return false;
        }),
        tap((success) => {
          if (success) {
            this.tokenRefreshService.startAutoRefresh();
            // Iniciar el monitoreo de expiración del token
            this.tokenExpirationMonitor.startMonitoring();
          }
        }),
      );
  }

  getProviderId(): number | string {
    try {
      const userDataString = localStorage.getItem('auth_user');
      if (!userDataString) {
        return 0;
      }

      const userData = JSON.parse(userDataString);
      const providerId = userData?.provider_id;

      return providerId || 0;
    } catch (error) {
      console.error('Error al obtener provider_id:', error);
      return 0;
    }
  }

  getUserPermissions(): Permission[] {
    const permissions = localStorage.getItem('user_permissions');
    return permissions ? JSON.parse(permissions) : [];
  }

  hasModuleAccess(moduleName: string): boolean {
    const permissions = this.getUserPermissions();
    //console.log(`🔍 hasModuleAccess(${moduleName}):`, permissions);

    const permission = permissions.find((p) => p.name === moduleName);
    //console.log(`🔍 Permiso encontrado para ${moduleName}:`, permission);

    return permission ? permission.display === 'on' : false;
  }


  getFirstAvailableRoute(): string {
    const routePermissionMap = [
      { route: '/dashboard', permission: 'provider_dashboard' },
      { route: '/dashboard', permission: 'commercial_dashboard' },
      { route: '/pre-factura', permission: 'preinvoice' },
      { route: '/pre-factura', permission: 'preinvoice-providers' },
      { route: '/pre-factura-sin-oc', permission: 'preinvoice' },
      { route: '/aprobacion-factura', permission: 'preinvoiceapproval' },
      { route: '/liquidaciones', permission: 'settlementcoes' },
      { route: '/homologacion', permission: 'coesconcept' },
      { route: '/conexiones', permission: 'connections' },
      { route: '/proveedores', permission: 'providers' },
      { route: '/usuarios', permission: 'users' },
      { route: '/roles-y-permisos', permission: 'roles' },
      { route: '/zona-horaria', permission: 'cron-config' },
    ];

    for (const item of routePermissionMap) {
      if (this.hasModuleAccess(item.permission)) {
        return item.route;
      }
    }

    return '/login';
  }


  getUserRoles(): string[] {
    try {
      const userDataString = localStorage.getItem('auth_user');
      if (!userDataString) {
        this.userRolesSubject.next([]);
        return [];
      }

      const userData = JSON.parse(userDataString);
      const roles = userData?.roles || [];

      // Actualizar el Subject
      this.userRolesSubject.next(roles);
      return roles;
    } catch (error) {
      console.error('Error al obtener roles del usuario:', error);
      this.userRolesSubject.next([]);
      return [];
    }
  }

  getOwnRoles(): Observable<UserRole[]> {
    return this.http.get<UserRole[]>(this.config.getEndpointUrl('ownRoles'));
  }

  setActiveRole(role: UserRole): void {
    this.activeRoleSubject.next(role);
  }

  getActiveRole(): Observable<UserRole | null> {
    return this.activeRoleSubject.asObservable();
  }


  hasRole(role: string): boolean {
    const userRoles = this.getUserRoles();
    console.log(`🔍 hasRole(${role}):`, userRoles);

    const hasRole = userRoles.includes(role);
    console.log(`🔍 Usuario tiene rol ${role}:`, hasRole);

    return hasRole;
  }

  /**
   * Observable para verificar si tiene un rol específico
   */
  hasRole$(role: string): Observable<boolean> {
    return new Observable((observer) => {
      this.userRoles$.subscribe((roles) => {
        const hasRole = roles.includes(role);
        observer.next(hasRole);
      });
    });
  }

  logout(): Observable<void> {
    // Detener la renovación auto y remove token
    this.tokenRefreshService.stopAutoRefresh();

    // Detener el monitoreo de expiración
    this.tokenExpirationMonitor.stopMonitoring();

    this.tokenStorage.clearToken();

    // Limpiar datos de usuario y permisos de localStorage
    localStorage.removeItem('user_permissions');
    localStorage.removeItem('auth_user');

    // Notificar cambios
    this.permissionsSubject.next([]);
    this.userRolesSubject.next([]);

    this.authStore.clear();
    return of(void 0);
  }

  isAuthenticated(): boolean {
    return this.tokenStorage.hasToken();
  }

  getUser(): Observable<User | null> {
    return this.authStore.getUser();
  }

  getUserValue(): User | null {
    return this.authStore.getUserValue();
  }

  getToken(): string | null {
    return this.tokenStorage.getToken();
  }

  getUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(this.config.getEndpointUrl('profileEndpoint')).pipe(
      map((response) => {
        return response;
      }),
      catchError((error) => {
        console.error('❌ Error al obtener perfil:', error);
        throw new Error('Error al obtener perfil de usuario');
      }),
    );
  }

  updateUserProfile(profileData: UpdateUserProfile): Observable<UserProfile> {
    return this.http
      .put<UserProfile>(this.config.getEndpointUrl('profileEndpoint'), profileData)
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) => {
          console.error('❌ Error al actualizar perfil:', error);
          throw error;
        }),
      );
  }

  register(userData: RegisterRequest): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<ApiResponse>(this.config.getEndpointUrl('registerEndpoint'), userData)
      .pipe(
        map((response) => {
          // Para API que devuelve mensaje de éxito sin datos de usuario
          if (response.message) {
            return {
              success: true,
              message: response.message,
            };
          }

          // Para API que devuelve datos de usuario (caso anterior)
          if (response.success && response.data) {
            return {
              success: true,
              message: 'Usuario registrado con éxito',
              data: response.data,
            };
          }

          throw new Error(response.message || 'Error en registro');
        }),
        catchError((error) => {
          console.error('Error en el registro:', error);
          return throwError(() => error);
        }),
      );
  }

  requestPasswordReset(email: string): Observable<boolean> {
    return this.http
      .post<ApiResponse>(this.config.getEndpointUrl('forgotPasswordEndpoint'), {
        email,
      })
      .pipe(
        map((response) => response.success),
        catchError(() => of(false)),
      );
  }

  resetPassword(token: string, newPassword: string): Observable<boolean> {
    const resetRequest: ResetPasswordRequest = {
      token,
      password: newPassword,
    };

    return this.http
      .post<ApiResponse>(this.config.getEndpointUrl('resetPasswordEndpoint'), resetRequest)
      .pipe(
        map((response) => response.success),
        catchError(() => of(false)),
      );
  }

  /**
   * Método para forzar la recarga de permisos desde localStorage
   * Útil para debug o situaciones donde necesites sincronizar manualmente
   */
  refreshPermissions(): void {
    const permissions = this.getUserPermissions();
    console.log('🔄 Refrescando permisos manualmente:', permissions);
    this.permissionsSubject.next(permissions);
  }

  private checkAuthentication(): void {
    if (this.tokenStorage.hasToken()) {
      // Verificar si el token no ha expirado
      if (!this.tokenStorage.isTokenExpired()) {
        // Reconstruir usuario desde localStorage y ponerlo en el store
        try {
          const userDataString = localStorage.getItem('auth_user');
          if (userDataString) {
            const userData = JSON.parse(userDataString);
            const roles = userData.roles || [];
            const user: User = {
              user_id: userData.user_id || 0,
              provider_id: userData.provider_id || 0,
              email: userData.email,
              username: userData.username,
              roles: roles,
            };

            // Restaurar usuario en el store
            this.authStore.setUser(user);

            // Actualizar roles subject
            this.userRolesSubject.next(roles);
          }
        } catch (error) {
          console.error('Error al restaurar usuario desde localStorage:', error);
        }

        // Cargar permisos si existen y notificar
        const permissions = this.getUserPermissions();
        if (permissions.length > 0) {
          //console.log('🔄 Restaurando permisos al inicializar:', permissions);
          this.permissionsSubject.next(permissions);
        }

        // Iniciar monitoreo de expiración si hay un token válido
        this.tokenExpirationMonitor.startMonitoring();
      } else {
        // Token expirado, limpiar todo
        console.warn('⚠️ Token expirado detectado al inicializar. Limpiando sesión...');
        this.tokenStorage.clearToken();
        localStorage.removeItem('user_permissions');
        localStorage.removeItem('auth_user');
        this.permissionsSubject.next([]);
        this.authStore.clear();
      }
    } else {
      // Si no hay token, asegurar que no hay permisos
      this.permissionsSubject.next([]);
      this.authStore.clear();
    }
  }
}
